import React, { useState, useEffect } from 'react'
import { getData, postData } from '../../helpers/api';
import moment from 'moment';
import { toast } from 'react-toastify';
import { txUrl } from '../../chain/config';
import { IMAGE_URL } from '../../Constants/constants';

const STEPS = ["Pending", "Accepted", "Processing", "Shipped", "Delivered"];

export default function Orders() {
    const [orderData, setOrderData] = useState([])
    const [openGroup, setOpenGroup] = useState(null);   // expanded group key
    const [payments, setPayments] = useState({});        // groupKey -> payment

    useEffect(() => {
        getOrders()
    }, [])

    const getOrders = () => {
        getData(`${process.env.REACT_APP_APP_API_URL}/api/order`).then(res => {
            setOrderData(res.data.orders || []);
        }).catch(() => {})
    }

    const toggleGroup = (key, groupId) => {
        const next = openGroup === key ? null : key;
        setOpenGroup(next);
        if (next && groupId && !payments[key]) {
            getData(`${process.env.REACT_APP_APP_API_URL}/api/order/track/${groupId}`).then(res => {
                if (res.data.status) setPayments(p => ({ ...p, [key]: res.data.payment }));
            }).catch(() => {});
        }
    };

    const cancelOrder = (line) => {
        if (!window.confirm('Cancel this order?')) return;
        postData(`${process.env.REACT_APP_APP_API_URL}/api/order/status`, { orderId: line._id, status: 'Cancelled' })
            .then(res => {
                if (res.data.status) { toast.success('Order cancelled'); getOrders(); }
                else toast.error(res.data.message || 'Could not cancel');
            }).catch(() => toast.error('Could not cancel'));
    };

    // group lines by checkout
    const groups = [];
    const byId = {};
    for (const o of orderData) {
        const key = o.orderGroupId || o._id;
        if (!byId[key]) { byId[key] = { key, groupId: o.orderGroupId, lines: [], createdAt: o.createdAt }; groups.push(byId[key]); }
        byId[key].lines.push(o);
    }

    const groupStatus = (g) => {
        // the least-advanced non-cancelled line drives the group's headline status
        const active = g.lines.filter(l => l.status !== 'Cancelled');
        if (!active.length) return 'Cancelled';
        let idx = STEPS.length - 1;
        for (const l of active) {
            const i = STEPS.indexOf(l.status);
            if (i >= 0 && i < idx) idx = i;
        }
        return STEPS[idx];
    };

    return (
        <div className="bp-orders">
            <h3 className="bp-orders-title">Your Orders</h3>
            {groups.length === 0 && <p className="bp-dim">No orders yet — everything you buy shows up here.</p>}
            {groups.map((g) => {
                const status = groupStatus(g);
                const total = g.lines.reduce((s, l) => s + (l.lineTotal || l.price * l.quantity || 0), 0);
                const first = g.lines[0];
                const ship = first.shipment || {};
                const open = openGroup === g.key;
                const stepIdx = STEPS.indexOf(status);
                const pay = payments[g.key];
                return (
                    <div className={`bp-order-card ${open ? 'open' : ''}`} key={g.key}>
                        <button className="bp-order-head" onClick={() => toggleGroup(g.key, g.groupId)}>
                            <div className="bp-order-head-main">
                                <span className="bp-order-id bp-mono">#{String(g.key).slice(-8)}</span>
                                <span className="bp-order-date">{moment(g.createdAt).format('MMM D, YYYY')}</span>
                                <span className="bp-order-items">{g.lines.length} item{g.lines.length > 1 ? 's' : ''}</span>
                            </div>
                            <div className="bp-order-head-side">
                                <span className="bp-mono bp-order-total">${total.toFixed(2)}</span>
                                <span className={`bp-order-status s-${status.toLowerCase()}`}>{status}</span>
                                <span className="bp-order-chevron">{open ? '▾' : '▸'}</span>
                            </div>
                        </button>

                        {open && (
                            <div className="bp-order-body">
                                {status !== 'Cancelled' && (
                                    <div className="bp-order-stepper">
                                        {STEPS.map((s, i) => (
                                            <div className={`bp-step ${i <= stepIdx ? 'done' : ''} ${i === stepIdx ? 'current' : ''}`} key={s}>
                                                <span className="bp-step-dot" />
                                                <span className="bp-step-label">{s}</span>
                                                {i < STEPS.length - 1 && <span className="bp-step-bar" />}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="bp-order-lines">
                                    {g.lines.map((line) => (
                                        <div className="bp-order-line" key={line._id}>
                                            <img alt="" src={line.product && line.product.image ? IMAGE_URL + line.product.image : '/images/placeholder.svg'} />
                                            <div className="bp-order-line-info">
                                                <strong>{line.product ? line.product.name : '—'}</strong>
                                                <span className="bp-dim">×{line.quantity} · ${line.price} each{line.seller && line.seller.businessName ? ` · ${line.seller.businessName}` : ''}</span>
                                            </div>
                                            <span className={`bp-order-status s-${(line.status || '').toLowerCase()}`}>{line.status}</span>
                                            {line.status === 'Pending' && (
                                                <button className="bp-btn-ghost bp-order-cancel" onClick={() => cancelOrder(line)}>Cancel</button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {(ship.carrier || ship.trackingNumber || (ship.events && ship.events.length > 0)) && (
                                    <div className="bp-tracking">
                                        <h5>Shipment</h5>
                                        {(ship.carrier || ship.trackingNumber) && (
                                            <p className="bp-tracking-carrier">
                                                {ship.carrier} <span className="bp-mono">{ship.trackingNumber}</span>
                                                {ship.trackingUrl && <> · <a href={ship.trackingUrl} target="_blank" rel="noreferrer">Track with carrier ↗</a></>}
                                            </p>
                                        )}
                                        {(ship.events || []).slice().reverse().map((ev, i) => (
                                            <div className="bp-tracking-event" key={i}>
                                                <span className="bp-tracking-dot" />
                                                <div>
                                                    <strong>{ev.status}</strong>
                                                    <span className="bp-dim"> {ev.location ? `· ${ev.location}` : ''}</span>
                                                    <div className="bp-dim bp-tracking-time">{moment(ev.at).format('MMM D, HH:mm')}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="bp-order-payment">
                                    {first.paymentMethod === 'BRC' ? (
                                        first.paymentStatus === 'paid' ? (
                                            <span>⛓ Paid on-chain
                                                {pay && pay.amountBRC && <> · <span className="bp-mono">{pay.amountBRC} BRC</span></>}
                                                {pay && pay.txHash && <> · <a href={txUrl(pay.txHash)} target="_blank" rel="noreferrer" className="bp-mono">
                                                    {pay.txHash.slice(0, 10)}…{pay.txHash.slice(-6)} ↗</a></>}
                                            </span>
                                        ) : <span className="bp-dim">BRC payment pending</span>
                                    ) : <span className="bp-dim">💵 Cash on delivery</span>}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    )
}
